import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

export default function PaymentIntentsPage() {
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, pending

  useEffect(() => {
    fetchIntents();
  }, []);

  const fetchIntents = async () => {
    try {
      const response = await axios.get('/payments/intents');
      setIntents(response.data);
    } catch (error) {
      console.error('Failed to fetch payment intents', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', label: '✓ Completato' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: '⏳ In Attesa' },
      failed: { color: 'bg-red-100 text-red-800', label: '✗ Fallito' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const filteredIntents = intents.filter(intent => {
    if (filter === 'all') return true;
    return intent.status === filter;
  });

  const stats = {
    total: intents.length,
    completed: intents.filter(i => i.status === 'completed').length,
    pending: intents.filter(i => i.status === 'pending').length,
    totalAmount: intents
      .filter(i => i.status === 'completed')
      .reduce((sum, i) => sum + i.amount, 0)
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-6 pt-24 pb-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Payment Intents Stripe</h1>
          <p className="text-gray-600">Visualizza e monitora tutti i pagamenti elaborati tramite Stripe</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <p className="text-sm text-gray-500">Totale Pagamenti</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              <p className="text-sm text-gray-500">Completati</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
              <p className="text-sm text-gray-500">In Attesa</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">€{stats.totalAmount.toFixed(2)}</div>
              <p className="text-sm text-gray-500">Totale Incassato</p>
            </CardContent>
          </Card>
        </div>

        {/* Filtri */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Tutti ({stats.total})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 rounded-lg ${filter === 'completed' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Completati ({stats.completed})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg ${filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            In Attesa ({stats.pending})
          </button>
        </div>

        {/* Tabella Payment Intents */}
        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : filteredIntents.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-gray-500">
              Nessun payment intent trovato
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Periodo Quota
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Importo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stripe ID
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredIntents.map((intent) => (
                    <tr key={intent.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(intent.created_at).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="font-medium text-gray-900">{intent.user_name}</div>
                        <div className="text-gray-500">{intent.customer_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {intent.quota_periodo || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        €{intent.amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(intent.status)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                        <div className="max-w-xs truncate" title={intent.stripe_payment_intent_id}>
                          {intent.stripe_payment_intent_id || intent.stripe_checkout_session_id}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Legenda */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">Legenda Status:</h3>
          <div className="flex gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              {getStatusBadge('completed')}
              <span className="text-sm text-gray-600">Pagamento completato e confermato da Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge('pending')}
              <span className="text-sm text-gray-600">In attesa di conferma o pagamento in corso</span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge('failed')}
              <span className="text-sm text-gray-600">Pagamento fallito o annullato</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
